import { useCallback, useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { TaskCreate } from "@common/contracts/task.contract";
import { SelectedTransaction } from "@components/transactions/TransactionsBulkActions/transactionsBulkActionsContext";
import { SlateValue } from "@components/mainInterface/chat/slate/slateTypes";
import {
    serializeSlateValue,
    isSlateValueEmpty,
} from "@components/mainInterface/chat/slate/slateHelpers";
import { MessageType } from "@common/types/chat";
import { TaskType } from "@common/types/task";
import { useAutofocus } from "@hooks/useAutofocus";
import styles from "./CreateTaskDrawer.module.scss";
import { TaskAttachment } from "./TaskAttachment";
import { TaskDrawerHeaderFields } from "./TaskDrawerHeaderFields";
import { TaskWorkflowField } from "./TaskWorkflowField";

interface TaskCreationFormTopSectionProps {
    transactionsTask?: {
        transactions?: SelectedTransaction[];
        transactionsCount?: number;
    };
    hideAttachment?: boolean;
    /** Bumped by the parent to force the description editor to remount with empty content. */
    descriptionResetKey?: number;
    isWorkflowLocked?: boolean;
    onTemplateApplied?: () => void;
    onWorkflowChange?: (type: TaskType) => void;
}

export const TaskCreationFormTopSection = ({
    transactionsTask,
    hideAttachment,
    descriptionResetKey,
    isWorkflowLocked,
    onTemplateApplied,
    onWorkflowChange,
}: TaskCreationFormTopSectionProps) => {
    const hasAttachment = !hideAttachment && !!transactionsTask;
    const formik = useFormikContext<TaskCreate>();
    const titleInputRef = useRef<HTMLInputElement>(null);

    useAutofocus(titleInputRef);

    const setFieldValueRef = useRef(formik.setFieldValue);
    useEffect(() => {
        setFieldValueRef.current = formik.setFieldValue;
    }, [formik.setFieldValue]);

    const handleDescriptionChange = useCallback((value: SlateValue) => {
        if (isSlateValueEmpty(value)) {
            setFieldValueRef.current("description", null);
            setFieldValueRef.current("descriptionType", undefined);
        } else {
            setFieldValueRef.current("description", serializeSlateValue(value));
            setFieldValueRef.current("descriptionType", MessageType.SLATE);
        }
    }, []);

    return (
        <div>
            {hasAttachment ? (
                <div className={styles.topSection}>
                    <TaskAttachment transactionsTask={transactionsTask} />
                </div>
            ) : null}
            <TaskDrawerHeaderFields<TaskCreate>
                titleName="title"
                titlePlaceholder="Task title"
                titleRef={titleInputRef}
                description={formik.values.description}
                onDescriptionChange={handleDescriptionChange}
                descriptionResetKey={descriptionResetKey}
                renderWorkflowPicker={(pickerProps) => (
                    <TaskWorkflowField
                        isLocked={isWorkflowLocked}
                        onTemplateApplied={onTemplateApplied}
                        onWorkflowChange={onWorkflowChange}
                        {...pickerProps}
                    />
                )}
            />
        </div>
    );
};
